// Map gestures with Pointer Events (touch, Pencil, mouse): one-finger drag pans with
// a glide on release, pinch zooms around the fingers, mouse wheel / trackpad pinch zooms
// around the cursor, right-drag pans. docs/06-UX.md → Planning (map gestures).
// Tap, long-press and two-finger-tap undo arrive with the tools in M5.

export type GestureTarget = {
  panBy(dx: number, dy: number): void;
  zoomAt(sx: number, sy: number, factor: number): void;
  beginGesture(): void;
  endGesture(v: { vx: number; vy: number } | null): void;
};

type Pt = { x: number; y: number };
type Sample = { x: number; y: number; t: number };

/** Velocity is measured over the last stretch of the drag. */
const VELOCITY_WINDOW_MS = 80;
/** A pause longer than this before lifting the finger cancels the glide. */
const RELEASE_PAUSE_MS = 60;

export function attachMapGestures(el: HTMLElement, target: GestureTarget): () => void {
  const pts = new Map<number, Pt>();
  let samples: Sample[] = [];
  let pinch: { dist: number; mid: Pt } | null = null;
  let flingAllowed = true;

  const local = (e: PointerEvent | WheelEvent): Pt => {
    const r = el.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const pinchState = (): { dist: number; mid: Pt } => {
    const [a, b] = [...pts.values()] as [Pt, Pt];
    return {
      dist: Math.max(1, Math.hypot(b.x - a.x, b.y - a.y)),
      mid: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 },
    };
  };

  const onDown = (e: PointerEvent): void => {
    if (e.pointerType === 'mouse' && e.button !== 0 && e.button !== 2) return;
    e.preventDefault();
    try {
      el.setPointerCapture(e.pointerId);
    } catch {
      // Synthetic or already-released pointers can't be captured; panning still works.
    }
    if (pts.size === 0) {
      target.beginGesture();
      flingAllowed = true;
    }
    pts.set(e.pointerId, local(e));
    samples = [];
    if (pts.size === 2) {
      pinch = pinchState();
      flingAllowed = false; // a pinch never ends in a glide
    }
  };

  const onMove = (e: PointerEvent): void => {
    const prev = pts.get(e.pointerId);
    if (!prev) return;
    e.preventDefault();
    const p = local(e);
    pts.set(e.pointerId, p);
    if (pts.size === 1) {
      target.panBy(p.x - prev.x, p.y - prev.y);
      samples.push({ x: p.x, y: p.y, t: e.timeStamp });
      const cutoff = e.timeStamp - VELOCITY_WINDOW_MS;
      while (samples.length > 2 && (samples[0] as Sample).t < cutoff) samples.shift();
    } else if (pts.size === 2 && pinch) {
      const now = pinchState();
      target.panBy(now.mid.x - pinch.mid.x, now.mid.y - pinch.mid.y);
      target.zoomAt(now.mid.x, now.mid.y, now.dist / pinch.dist);
      pinch = now;
    }
  };

  const onUp = (e: PointerEvent): void => {
    if (!pts.has(e.pointerId)) return;
    pts.delete(e.pointerId);
    if (pts.size < 2) pinch = null;
    if (pts.size === 1) {
      samples = []; // the remaining finger keeps panning from where it is
      return;
    }
    if (pts.size > 0) return;
    let v: { vx: number; vy: number } | null = null;
    const first = samples[0];
    const last = samples[samples.length - 1];
    if (
      flingAllowed &&
      e.type === 'pointerup' &&
      first &&
      last &&
      last.t > first.t &&
      e.timeStamp - last.t < RELEASE_PAUSE_MS
    ) {
      const dt = last.t - first.t;
      v = { vx: (last.x - first.x) / dt, vy: (last.y - first.y) / dt };
    }
    samples = [];
    target.endGesture(v);
  };

  const onWheel = (e: WheelEvent): void => {
    e.preventDefault();
    const p = local(e);
    // Trackpad pinch arrives as ctrl+wheel with small deltas; mouse wheels send ~100 per notch.
    const unit = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? 400 : 1;
    const k = e.ctrlKey ? 0.01 : 0.002;
    target.zoomAt(p.x, p.y, Math.exp(-e.deltaY * unit * k));
  };

  const noMenu = (e: Event): void => e.preventDefault();
  // iOS Safari's own pinch-zoom of the page (it ignores user-scalable=no in the browser).
  const noGesture = (e: Event): void => e.preventDefault();

  el.addEventListener('pointerdown', onDown);
  el.addEventListener('pointermove', onMove);
  el.addEventListener('pointerup', onUp);
  el.addEventListener('pointercancel', onUp);
  el.addEventListener('wheel', onWheel, { passive: false });
  el.addEventListener('contextmenu', noMenu);
  document.addEventListener('gesturestart', noGesture);
  return () => {
    el.removeEventListener('pointerdown', onDown);
    el.removeEventListener('pointermove', onMove);
    el.removeEventListener('pointerup', onUp);
    el.removeEventListener('pointercancel', onUp);
    el.removeEventListener('wheel', onWheel);
    el.removeEventListener('contextmenu', noMenu);
    document.removeEventListener('gesturestart', noGesture);
  };
}
